import { useState, useEffect, useCallback } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-master/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import {
  Brain, Zap, DollarSign, Search as SearchIcon, RefreshCw, Settings, Shield,
  AlertTriangle, CheckCircle2, XCircle, TrendingUp, Activity, Edit
} from 'lucide-react';
import apiClient from '@/lib/api';
import { toErr } from '@easyflow/shared';
import { useToast } from '@/hooks/use-toast';

interface QuotaConfig {
  id?: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_slug?: string;
  daily_token_limit: number;
  monthly_cost_limit: number;
  rag_queries_per_hour: number;
  alert_threshold_pct: number;
  throttle_threshold_pct: number;
  block_threshold_pct: number;
  throttle_delay_ms: number;
  enabled: boolean;
  notes: string;
}

interface AggregatedRow {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  daily_token_limit: number | null;
  monthly_cost_limit: number | null;
  rag_queries_per_hour: number | null;
  quota_status: string | null;
  quota_enabled: boolean | null;
  daily_tokens_used: number;
  monthly_cost_used: number;
  rag_queries_last_hour: number;
  daily_token_pct: number;
  monthly_cost_pct: number;
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="outline">Nessuna quota</Badge>;
  const map: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    active: { variant: 'default', label: 'Attivo' },
    alert: { variant: 'secondary', label: 'Alert' },
    throttled: { variant: 'secondary', label: 'Rallentato' },
    blocked: { variant: 'destructive', label: 'Bloccato' },
  };
  const info = map[status] || { variant: 'outline' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

export default function AIQuotasMaster() {
  const { toast } = useToast();
  const [aggregated, setAggregated] = useState<AggregatedRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTenantId, setEditTenantId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<QuotaConfig>({
    tenant_id: '',
    daily_token_limit: 500000,
    monthly_cost_limit: 50,
    rag_queries_per_hour: 100,
    alert_threshold_pct: 80,
    throttle_threshold_pct: 95,
    block_threshold_pct: 100,
    throttle_delay_ms: 2000,
    enabled: true,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<AggregatedRow[]>('/master/ai-quotas/aggregated');
      if (res.success && res.data) {
        setAggregated(res.data);
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void loadData(); }, [loadData]);

  const openEdit = async (tenantId: string, tenantName: string) => {
    setEditTenantId(tenantId);
    try {
      const res = await apiClient.get<{ quota: QuotaConfig | null }>(`/master/ai-quotas/${tenantId}`);
      if (res.success && res.data?.quota) {
        setEditForm({ ...res.data.quota, tenant_id: tenantId });
      } else {
        setEditForm({
          tenant_id: tenantId,
          daily_token_limit: 500000,
          monthly_cost_limit: 50,
          rag_queries_per_hour: 100,
          alert_threshold_pct: 80,
          throttle_threshold_pct: 95,
          block_threshold_pct: 100,
          throttle_delay_ms: 2000,
          enabled: true,
          notes: '',
        });
      }
    } catch {
      setEditForm({
        tenant_id: tenantId,
        daily_token_limit: 500000,
        monthly_cost_limit: 50,
        rag_queries_per_hour: 100,
        alert_threshold_pct: 80,
        throttle_threshold_pct: 95,
        block_threshold_pct: 100,
        throttle_delay_ms: 2000,
        enabled: true,
        notes: '',
      });
    }
    setEditOpen(true);
  };

  const saveQuota = async () => {
    if (!editTenantId) return;
    setSaving(true);
    try {
      const res = await apiClient.put(`/master/ai-quotas/${editTenantId}`, editForm);
      if (res.success) {
        toast({ title: 'Salvato', description: 'Quota AI aggiornata' });
        setEditOpen(false);
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore nel salvataggio', variant: 'destructive' });
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = aggregated.filter(r =>
    r.tenant_name.toLowerCase().includes(search.toLowerCase()) ||
    r.tenant_slug.toLowerCase().includes(search.toLowerCase())
  );

  const totalTokensToday = aggregated.reduce((s, r) => s + r.daily_tokens_used, 0);
  const totalCostMonth = aggregated.reduce((s, r) => s + r.monthly_cost_used, 0);
  const activeQuotas = aggregated.filter(r => r.quota_enabled).length;
  const alertCount = aggregated.filter(r => r.quota_status === 'alert' || r.quota_status === 'throttled' || r.quota_status === 'blocked').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            Quote AI per Tenant
          </h1>
          <p className="text-muted-foreground mt-1">Gestisci i limiti di consumo AI per ogni tenant</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Token Oggi (Totale)</p>
                <p className="text-2xl font-bold">{formatTokens(totalTokensToday)}</p>
              </div>
              <Zap className="h-8 w-8 text-yellow-500" />
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Costo Mese (Totale)</p>
                <p className="text-2xl font-bold">${totalCostMonth.toFixed(2)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Quote Attive</p>
                <p className="text-2xl font-bold">{activeQuotas}</p>
              </div>
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Alert / Bloccati</p>
                <p className="text-2xl font-bold">{alertCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <GlassCardTitle>Consumo AI Aggregato per Tenant</GlassCardTitle>
            <div className="relative w-full sm:w-64">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca tenant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">Tenant</th>
                    <th className="pb-3 font-medium">Token Oggi</th>
                    <th className="pb-3 font-medium">Costo Mese</th>
                    <th className="pb-3 font-medium">RAG/h</th>
                    <th className="pb-3 font-medium">Stato</th>
                    <th className="pb-3 font-medium">Quota</th>
                    <th className="pb-3 font-medium text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const maxPct = Math.max(row.daily_token_pct, row.monthly_cost_pct);
                    return (
                      <tr key={row.tenant_id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-3">
                          <div>
                            <p className="font-medium">{row.tenant_name}</p>
                            <p className="text-xs text-muted-foreground">{row.tenant_slug}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="space-y-1">
                            <span>{formatTokens(row.daily_tokens_used)}</span>
                            {row.daily_token_limit && (
                              <div className="w-24">
                                <Progress value={Math.min(row.daily_token_pct, 100)} className="h-1.5" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="space-y-1">
                            <span>${row.monthly_cost_used.toFixed(2)}</span>
                            {row.monthly_cost_limit && (
                              <div className="w-24">
                                <Progress value={Math.min(row.monthly_cost_pct, 100)} className="h-1.5" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3">{row.rag_queries_last_hour}</td>
                        <td className="py-3"><StatusBadge status={row.quota_status} /></td>
                        <td className="py-3">
                          {row.quota_enabled ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/70" />
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(row.tenant_id, row.tenant_name)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted-foreground">
                        Nessun tenant trovato
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configura Quota AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Quota abilitata</Label>
              <Switch
                checked={editForm.enabled}
                onCheckedChange={(v) => setEditForm(f => ({ ...f, enabled: v }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Limite token giornaliero</Label>
                <Input
                  type="number"
                  value={editForm.daily_token_limit}
                  onChange={(e) => setEditForm(f => ({ ...f, daily_token_limit: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Limite costo mensile ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.monthly_cost_limit}
                  onChange={(e) => setEditForm(f => ({ ...f, monthly_cost_limit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Query RAG per ora</Label>
              <Input
                type="number"
                value={editForm.rag_queries_per_hour}
                onChange={(e) => setEditForm(f => ({ ...f, rag_queries_per_hour: parseInt(e.target.value) || 0 }))}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Alert (%)</Label>
                <Input
                  type="number"
                  value={editForm.alert_threshold_pct}
                  onChange={(e) => setEditForm(f => ({ ...f, alert_threshold_pct: parseInt(e.target.value) || 80 }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Throttle (%)</Label>
                <Input
                  type="number"
                  value={editForm.throttle_threshold_pct}
                  onChange={(e) => setEditForm(f => ({ ...f, throttle_threshold_pct: parseInt(e.target.value) || 95 }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Block (%)</Label>
                <Input
                  type="number"
                  value={editForm.block_threshold_pct}
                  onChange={(e) => setEditForm(f => ({ ...f, block_threshold_pct: parseInt(e.target.value) || 100 }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Delay throttle (ms)</Label>
              <Input
                type="number"
                value={editForm.throttle_delay_ms}
                onChange={(e) => setEditForm(f => ({ ...f, throttle_delay_ms: parseInt(e.target.value) || 2000 }))}
              />
            </div>

            <div className="space-y-1">
              <Label>Note</Label>
              <Input
                value={editForm.notes || ''}
                onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Note opzionali..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveQuota} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
